import { StoreOverride } from '../types/StoreMap';

/**
 * Deep merge function that merges store-specific translations into base translations
 * Store translations take precedence over base translations
 */
function deepMerge(target: any, source: any): any {
  if (source === null || source === undefined) {
    return target;
  }

  if (typeof source !== 'object' || typeof target !== 'object') {
    return source;
  }

  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key])) {
        result[key] = deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
}

/**
 * Resolves translations by merging store-specific overrides with base translations
 *
 * @param baseTranslations - The base translation resources (from standard locale files)
 * @param storeOverride - Store-specific configuration that may contain translation overrides
 * @param language - The target language to resolve translations for
 * @returns Merged translation object with store overrides applied
 */
export function resolveStoreTranslations(
  baseTranslations: any,
  storeOverride: StoreOverride | undefined,
  language: string
): any {
  if (!storeOverride?.translations?.[language]) {
    // No store-specific translations for this language, return base translations
    return baseTranslations;
  }

  const storeTranslations = storeOverride.translations[language];
  const result = { ...baseTranslations };

  // For each namespace in store overrides, find matching base namespace
  for (const storeNamespace in storeTranslations) {
    if (storeTranslations.hasOwnProperty(storeNamespace)) {
      // Check if this namespace exists in base
      if (result[storeNamespace]) {
        // Merge into existing namespace
        result[storeNamespace] = deepMerge(result[storeNamespace], storeTranslations[storeNamespace]);
      } else {
        // Try to find a matching base namespace by removing suffixes (e.g., rayban-q339 -> rayban)
        const baseNamespace = Object.keys(result).find(key =>
          storeNamespace.startsWith(key + '-')
        );

        if (baseNamespace) {
          // First copy all base namespace keys, then apply store overrides
          result[storeNamespace] = deepMerge(result[baseNamespace], storeTranslations[storeNamespace]);
        } else {
          // No match found, add as new namespace
          result[storeNamespace] = storeTranslations[storeNamespace];
        }
      }
    }
  }

  return result;
}

/**
 * Creates a translation resource resolver that can be used with i18next
 * This function returns a resource resolver that applies store-specific overrides
 *
 * @param storeOverride - Store-specific configuration
 * @returns Function that resolves translations for a given language
 */
export function createStoreTranslationResolver(storeOverride: StoreOverride | undefined) {
  return (language: string, baseTranslations: any) => {
    return resolveStoreTranslations(baseTranslations, storeOverride, language);
  };
}
